import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, TrendingUp } from "lucide-react";

/**
 * CARS Skill Analytics card — shows per-type accuracy (Foundations /
 * Within-Text / Beyond-Text) and calls out the weakest skill when the user
 * has enough attempts.
 *
 * Renders nothing until the user has at least one CARS attempt so it never
 * shows an empty "0%" state to first-time visitors.
 */
export default function CARSSkillCard({ compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/cars/skill-stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || data.totals.attempted === 0) return null;

  const totals = data.totals;
  const types = data.by_type;
  const overallPct = Math.round(totals.accuracy * 100);
  const barColor = (acc) => {
    if (acc >= 0.8) return "#4f7942";
    if (acc >= 0.6) return "#7ba55c";
    if (acc >= 0.4) return "#d9a552";
    return "#d96c4a";
  };

  return (
    <div className="pm-card p-5" data-testid="cars-skill-card">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-display font-bold text-lg leading-tight">
            CARS by skill type
          </div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            AAMC targets ~30% Foundations · 40% Within-Text · 30% Beyond-Text.
            Track each skill separately to see where to focus.
          </div>
        </div>
        <div className="text-right">
          <div
            className="font-display text-2xl font-bold"
            style={{ color: barColor(totals.accuracy) }}
            data-testid="cars-skill-overall"
          >
            {overallPct}%
          </div>
          <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
            {totals.correct}/{totals.attempted} correct
          </div>
        </div>
      </div>

      {/* Weakest-skill callout — only when meaningful */}
      {data.message && (
        <div
          className="p-3 rounded-lg mb-4 flex items-start gap-2 text-sm"
          style={{ background: "var(--hl-yellow)", color: "var(--text)" }}
          data-testid="cars-skill-callout"
        >
          <AlertTriangle
            size={16}
            style={{ color: "#d97706", flexShrink: 0, marginTop: 2 }}
          />
          <span>{data.message}</span>
        </div>
      )}

      {/* Per-type bars */}
      <div className="space-y-3" data-testid="cars-skill-rows">
        {Object.entries(types).map(([tid, t]) => {
          const pct = Math.round(t.accuracy * 100);
          const barW = t.attempted > 0 ? pct : 0;
          const tone = t.attempted > 0 ? barColor(t.accuracy) : "var(--muted)";
          return (
            <div key={tid} data-testid={`cars-skill-${tid}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <div>
                  <span className="font-semibold">{t.label}</span>
                  <span
                    className="text-xs ml-2"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {t.attempted === 0
                      ? "no attempts yet"
                      : `${t.correct}/${t.attempted} · ${pct}%`}
                  </span>
                </div>
                <span
                  className="text-[10px] font-mono"
                  style={{ color: "var(--text-muted)" }}
                >
                  target ~{t.target_pct}%
                </span>
              </div>
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ background: "var(--muted)" }}
              >
                <div
                  className="h-full transition-all"
                  style={{ width: `${Math.max(2, barW)}%`, background: tone }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {!compact && overallPct >= 75 && (
        <div
          className="mt-4 flex items-center gap-2 text-xs"
          style={{ color: "var(--mastery)" }}
        >
          <TrendingUp size={14} /> Strong across the board — keep the CARS
          streak going.
        </div>
      )}
    </div>
  );
}
